import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, Grid, Alert, CircularProgress, Avatar, LinearProgress,
} from '@mui/material';
import { PersonOutline, School, Phone, Save, CheckCircle } from '@mui/icons-material';
import useAuthStore from '../store/useStore';
import { authAPI } from '../api/client';

function getRequiredFields(user) {
  const missing = [];
  if (!user?.phone) missing.push('phone');
  if (user?.role === 'tenant') {
    if (!user?.university) missing.push('university');
    if (!user?.student_id) missing.push('student_id');
  }
  if (user?.role === 'landlord' || user?.role === 'agent') {
    if (!user?.company_name) missing.push('company_name');
  }
  return missing;
}

function ProfileCompletionPrompt() {
  const { user, isAuthenticated, setUser } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setOpen(false);
      return;
    }
    // Only prompt KYC-verified users (admins are excluded)
    if (user.role === 'super_admin' || user.role === 'rent_control') return;
    if (!user.is_verified) return;

    // Check if dismissed this session
    const sessionKey = `profile_prompt_dismissed_${user.id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const missing = getRequiredFields(user);
    if (missing.length > 0) {
      const defaults = {};
      missing.forEach(f => { defaults[f] = user[f] || ''; });
      setForm(defaults);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isAuthenticated, user]);

  const missing = getRequiredFields(user);
  const totalRequired = user?.role === 'tenant' ? 3 : (user?.role === 'landlord' || user?.role === 'agent') ? 2 : 1;
  const completed = totalRequired - missing.length;
  const progress = Math.round((completed / totalRequired) * 100);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await authAPI.updateMe(form);
      setUser(res.data);
      const newMissing = getRequiredFields(res.data);
      if (newMissing.length === 0) {
        setOpen(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    if (user?.id) {
      sessionStorage.setItem(`profile_prompt_dismissed_${user.id}`, 'true');
    }
    setDismissed(true);
    setOpen(false);
  };

  if (!open || dismissed) return null;

  const fieldLabels = {
    phone: { label: 'Phone Number', placeholder: '+233XXXXXXXXX', icon: <Phone /> },
    university: { label: 'University', placeholder: 'e.g. University of Ghana', icon: <School /> },
    student_id: { label: 'Student ID', placeholder: 'e.g. STU2024001', icon: <PersonOutline /> },
    company_name: { label: 'Company / Business Name', placeholder: 'e.g. Prime Properties Ltd', icon: <PersonOutline /> },
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1e40af', color: 'white', px: 3, py: 3, textAlign: 'center' }}>
        <Avatar sx={{ mx: 'auto', mb: 1.5, width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)' }}>
          <PersonOutline sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" fontWeight={700}>Complete Your Profile</Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
          Your identity has been verified! Please fill in the remaining details to get the best experience.
        </Typography>
        <Box sx={{ mt: 2, px: 4 }}>
          <LinearProgress variant="determinate" value={progress}
            sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': { bgcolor: '#4ade80', borderRadius: 4 } }} />
          <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
            {completed}/{totalRequired} fields completed
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {user?.role === 'tenant'
            ? 'We use your university and location to help you find accommodation nearby.'
            : 'Complete your profile so tenants can reach you easily.'}
        </Typography>

        <Grid container spacing={2}>
          {missing.map(field => {
            const meta = fieldLabels[field] || { label: field, placeholder: '', icon: null };
            return (
              <Grid item xs={12} key={field}>
                <TextField
                  fullWidth
                  label={meta.label}
                  placeholder={meta.placeholder}
                  value={form[field] || ''}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                  InputProps={{
                    startAdornment: meta.icon ? (
                      <Box sx={{ mr: 1, display: 'flex', color: 'action.active' }}>{meta.icon}</Box>
                    ) : null,
                  }}
                  required
                />
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb', justifyContent: 'space-between' }}>
        <Button onClick={handleDismiss} color="inherit" size="small">
          Remind Me Later
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || missing.some(f => !form[f]?.trim())}
          startIcon={saving ? <CircularProgress size={18} /> : <Save />}
          sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1e3a8a' } }}
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProfileCompletionPrompt;
